import { useEffect, useMemo, useState } from "react";

const TOPICS = ["all", "General", "Workshops", "1:1 Sessions", "Collaboration", "Press / Media", "Other"];

export default function AdminContact() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [topic, setTopic] = useState("all");
  const [expandedId, setExpandedId] = useState(null);

  async function loadContacts() {
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/.netlify/functions/get-contacts", {
        method: "GET",
        credentials: "include",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        throw new Error(data.error || "Failed to load contact submissions");
      }
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch (err) {
      setError(err.message || "Failed to load contact submissions");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadContacts();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((item) => {
      if (topic !== "all" && (item.topic || "") !== topic) return false;
      if (!q) return true;
      return (
        item.name.toLowerCase().includes(q) ||
        item.email.toLowerCase().includes(q) ||
        item.message.toLowerCase().includes(q) ||
        (item.topic || "").toLowerCase().includes(q)
      );
    });
  }, [items, query, topic]);

  return (
    <div className="admin-events">
      <h1>Contact Submissions</h1>

      <div className="ae-toolbar">
        <p className="ae-hint">
          {loading
            ? "Loading…"
            : `${filtered.length} of ${items.length} submission${items.length === 1 ? "" : "s"}`}
        </p>
        <input
          type="search"
          className="ae-input"
          placeholder="Search name, email, message…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <select
          className="ae-input"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
        >
          {TOPICS.map((t) => (
            <option key={t} value={t}>
              {t === "all" ? "All topics" : t}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="ae-button ae-button-outline"
          onClick={loadContacts}
          disabled={loading}
        >
          Refresh
        </button>
      </div>

      {!loading && error && <p className="ae-error">{error}</p>}
      {!loading && !error && items.length === 0 && (
        <p className="ae-hint">No contact submissions yet.</p>
      )}
      {!loading && !error && items.length > 0 && filtered.length === 0 && (
        <p className="ae-hint">No submissions match your filters.</p>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div className="ae-notification-list">
          {filtered.map((item) => {
            const expanded = expandedId === item.id;
            return (
              <article key={item.id} className="ae-notification-card">
                <div className="ae-notification-main">
                  <div className="ae-notification-top">
                    <strong>{item.name || "(no name)"}</strong>
                    {item.topic ? <span className="ae-pill-new">{item.topic}</span> : null}
                  </div>
                  <span>
                    <a href={`mailto:${item.email}`}>{item.email || "(no email)"}</a>
                  </span>
                  <small>
                    {item.ts ? new Date(item.ts).toLocaleString() : "Unknown time"}
                  </small>
                  <p
                    style={{
                      marginTop: 8,
                      whiteSpace: "pre-wrap",
                      display: expanded ? "block" : "-webkit-box",
                      WebkitLineClamp: expanded ? "unset" : 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {item.message}
                  </p>
                  {item.message && item.message.length > 160 && (
                    <button
                      type="button"
                      className="ae-button ae-button-outline"
                      style={{ marginTop: 8, alignSelf: "flex-start" }}
                      onClick={() => setExpandedId(expanded ? null : item.id)}
                    >
                      {expanded ? "Show less" : "Show more"}
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
